import { Input } from '@/components/ui/input';

/**
 * Text input constrained to a valid decimal money string (e.g. "1234.56").
 *
 * Money is stored and transmitted as decimal strings throughout the AR stack,
 * so this emits a string rather than a number — avoiding float rounding. Emits
 * `null` when the field is cleared.
 */
export function DecimalInput({
  value,
  onChange,
  placeholder = '0.00',
  id,
  disabled,
  className,
  maxFractionDigits = 2,
  autoFocus,
}: {
  value: string | null;
  onChange: (value: string | null) => void;
  placeholder?: string;
  id?: string;
  disabled?: boolean;
  className?: string;
  maxFractionDigits?: number;
  autoFocus?: boolean;
}) {
  function handleChange(raw: string) {
    // Normalize before validating: pasted amounts routinely carry currency
    // symbols, thousands separators, or spaces ("$29,797.75") and accounting
    // negatives ("(1,234.56)"). Without this, the strict pattern below
    // rejects the whole change and paste appears to do nothing.
    let trimmed = raw.trim().replace(/[$,\s]/g, '');
    const parenNegative = /^\((.*)\)$/.exec(trimmed);
    if (parenNegative) trimmed = `-${parenNegative[1]}`;
    if (trimmed === '') {
      onChange(null);
      return;
    }
    // Allow an optional leading minus, digits, and a single decimal point with
    // up to `maxFractionDigits` fractional digits. Reject anything else so the
    // value stays a parseable decimal string. Clamp the bound defensively so a
    // bad prop (NaN, negative) can't produce an invalid quantifier that throws.
    const fractionDigits =
      Number.isInteger(maxFractionDigits) && maxFractionDigits >= 0
        ? maxFractionDigits
        : 2;
    const pattern = new RegExp(`^-?\\d*(\\.\\d{0,${fractionDigits}})?$`);
    if (!pattern.test(trimmed)) return;
    onChange(trimmed);
  }

  return (
    <Input
      id={id}
      inputMode="decimal"
      value={value ?? ''}
      onChange={(e) => handleChange(e.target.value)}
      placeholder={placeholder}
      disabled={disabled}
      className={className}
      autoFocus={autoFocus}
    />
  );
}
